import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import UnitCheckGroup from '@/components/controls/UnitCheckGroup';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import File from '@/components/ui/file';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { CONTROL_DEFAULT_STATE } from '@/constants';
import { ControlContext } from '@/contexts/ControlContext';
import useCognitoUsers from '@/hooks/useCognitoUsers';
import { useControlAckById, useControlById } from '@/hooks/useControlsAPI';
import { useNotificationModal } from '@/hooks/useNotificationModal';
import PageLayout from '@/layouts/PageLayout';
import { getUserInfo } from '@/lib/auth';
import { cn } from '@/lib/utils';

import {
  ProgressionActionsHead,
  ProgressionFormCompletionAction,
  ProgressionRecordsDelete,
  ProgressionRecordsUpload,
} from './ControlActions';

const ReviewerTable = ({ id }) => {
  const { ack } = useControlAckById(id);
  const { getBySub } = useCognitoUsers();

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="font-semibold">Name</TableHead>
          <TableHead className="font-semibold">Email</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {ack?.pendingUsers.map(({ userSub }) => (
          <TableRow key={userSub}>
            <TableCell>{getBySub(userSub)?.name}</TableCell>
            <TableCell>{getBySub(userSub)?.email}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const ControlProgressPage = () => {
  const { id } = useParams();
  const [form, setForm] = useState(CONTROL_DEFAULT_STATE);
  const { showNotificationModal } = useNotificationModal();

  const { ack } = useControlAckById(id);
  const { control, refetch } = useControlById(id);
  const isQa = getUserInfo()?.['cognito:groups']?.includes('QA');

  const setFormByKey = useCallback((key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const handleReviewersClick = () => {
    showNotificationModal(
      'Incomplete "Read and Understood" List',
      <ReviewerTable id={id} />
    );
  };

  const handleAttachmentChange = ({ files }) => {
    setFormByKey('recordInput', files);
  };

  useEffect(() => {
    if (control) {
      if (!form.id) setForm(() => ({ ...control }));
      else if (control.isComplete !== form.isComplete)
        setForm(() => ({ ...form, isComplete: control.isComplete }));
      else if (
        JSON.stringify(control.trainingRecords) !==
        JSON.stringify(form.trainingRecords)
      )
        setForm(() => ({ ...form, trainingRecords: control.trainingRecords }));
    }
  }, [control, form]);

  return (
    <ControlContext.Provider value={{ form, id, refetch, setFormByKey }}>
      <PageLayout
        className="mt-14"
        title={`Change Control Form: ${form.serial}`}
        toolbarHead={<ProgressionActionsHead />}
      >
        <div className="pb-6 border-b">
          <h4 className="text-sm font-semibold text-muted-foreground mb-3">
            Read and Understood Progression
          </h4>
          <div className="flex flex-row gap-4 items-center">
            <div className="bg-gray-200 h-4 relative rounded-xl w-full">
              <span
                className={cn('absolute bg-emerald-500 h-full rounded-xl')}
                style={{
                  width: `${ack?.percentComplete}%`,
                }}
              />
            </div>
            <div className="font-semibold text-xl">{ack?.percentComplete}%</div>
          </div>
          <div className="flex flex-row items-center justify-between mt-3">
            <div>Deadline: {control?.trainingDeadline}</div>
            <div className="flex gap-3">
              {isQa && (
                <Button onClick={handleReviewersClick} variant="outline">
                  Users who have yet read and understood
                </Button>
              )}
            </div>
          </div>
        </div>
        <h4 className="my-3 text-sm font-semibold text-muted-foreground">
          Additional Training Requirements
        </h4>
        <Card className="flex mb-4">
          <CardHeader className="pb-6 grow">
            <div className="flex gap-4 items-center justify-between">
              <CardTitle className="text-base font-medium w-56">
                <p className="mb-4">Required Attendees</p>
                <UnitCheckGroup
                  className="grid-cols-1"
                  disabled
                  name="trainingAttendees"
                />
              </CardTitle>
              <CardDescription className="flex flex-col grow h-full">
                <p className="font-medium mb-4 text-black text-base">
                  Training Information
                </p>
                <Textarea
                  className="font-mono grow resize-none"
                  disabled
                  onChange={({ target }) =>
                    setFormByKey('trainingInformation', target.value)
                  }
                  placeholder="Click to describe the training to be conducted (e.g., face to face onsite training, role play)"
                  value={form.trainingInformation}
                />
              </CardDescription>
            </div>
          </CardHeader>
        </Card>
        <Card className="flex mb-4">
          <CardHeader className="pb-6 grow">
            <div className="flex gap-4 items-center justify-between">
              <CardTitle className="text-base font-medium w-56">
                Training Records
              </CardTitle>
              <CardDescription className="grow space-y-2">
                {form.trainingRecords && (
                  <div>
                    {form.trainingRecords.map(({ key, originalName }) => (
                      <p className="flex hover:text-neutral-950" key={key}>
                        <span className="grow">{originalName}</span>
                        <ProgressionRecordsDelete id={key} />
                      </p>
                    ))}
                  </div>
                )}
                <File
                  ignoreBase64
                  multiple
                  onChange={handleAttachmentChange}
                  value={form.recordInput}
                />
                <ProgressionRecordsUpload disabled={!form.recordInput} />
              </CardDescription>
            </div>
          </CardHeader>
        </Card>
        <Card className="flex mb-4">
          <CardHeader className="pb-6 grow">
            <div className="flex gap-4 items-center justify-between">
              <CardTitle className="text-base font-medium w-56">
                Training Completion
              </CardTitle>
              <CardDescription className="grow">
                <ProgressionFormCompletionAction />
              </CardDescription>
            </div>
          </CardHeader>
        </Card>
      </PageLayout>
    </ControlContext.Provider>
  );
};

export default ControlProgressPage;
